/*
 * Left-to-right process-flow layout (pure — no Office.js imports, unit-tested).
 *
 * Chevron primitives fill the region width with fixed gaps, vertically
 * centered; the first chevron gets the primary fill, the rest alternate
 * secondary/accent. Optional per-step detail renders below each chevron.
 */

import {
  BoxPrim,
  DEFAULT_PALETTE,
  DEFAULT_REGION,
  Palette,
  Primitive,
  Region,
  TextPrim,
} from "./primitives";

export interface ProcessStep {
  label: string;
  detail?: string;
}

/* Layout constants (points). */
const GAP = 8;
const CHEV_H = 64;
const DETAIL_H = 40;
const DETAIL_GAP = 6;
const GRAY = "64748B";

export function processFlowLayout(steps: ProcessStep[], region?: Region, palette?: Palette): Primitive[] {
  const reg = region || DEFAULT_REGION;
  const pal = palette || DEFAULT_PALETTE;
  if (!steps || steps.length === 0) return [];

  const n = steps.length;
  const chevW = (reg.w - (n - 1) * GAP) / n;
  const y = reg.y + (reg.h - CHEV_H) / 2;

  const prims: Primitive[] = [];
  for (let i = 0; i < n; i++) {
    const step = steps[i];
    const x = reg.x + i * (chevW + GAP);
    const fill = i === 0 ? pal.primary : i % 2 === 1 ? pal.secondary : pal.accent;
    const fontColor = i === 0 || i % 2 === 0 ? pal.paper : pal.ink;
    prims.push({
      kind: "chevron",
      x: x,
      y: y,
      w: chevW,
      h: CHEV_H,
      text: step.label,
      style: { fill: fill, fontColor: fontColor, fontSize: 12, bold: i === 0, align: "center" },
    } as BoxPrim);
    if (step.detail) {
      prims.push({
        kind: "text",
        x: x,
        y: y + CHEV_H + DETAIL_GAP,
        w: chevW,
        h: DETAIL_H,
        text: step.detail,
        style: { fontSize: 9, fontColor: GRAY, align: "center" },
      } as TextPrim);
    }
  }
  return prims;
}
